import React, { useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { CloudUpload, ExpandMore } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../api";
import WizardPageShell from "./WizardPageShell";

const POLL_MS = 3000;
const MAX_POLLS = 60;

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function WizardAiMarking() {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("capture"); // capture | processing | results
  const [statusMessage, setStatusMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");

  const reset = () => {
    setFiles([]);
    setPhase("capture");
    setStatusMessage("");
    setSummary(null);
    setQuestions([]);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPick = (list) => {
    const next = Array.from(list || []).slice(0, 8);
    setFiles(next);
    setError("");
  };

  const startMarking = async () => {
    if (files.length === 0) {
      toast.error("Upload at least one image or PDF.");
      return;
    }
    setPhase("processing");
    setError("");
    setStatusMessage("Uploading your paper…");
    const form = new FormData();
    files.forEach((f) => form.append("files[]", f));
    try {
      const res = await api.post("student/wizard/ai-marking/mark", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const sessionId = String(res.data?.data?.session_id || "");
      if (!sessionId) throw new Error("Missing session.");
      setStatusMessage("AI is marking your paper…");
      for (let i = 0; i < MAX_POLLS; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(POLL_MS);
        // eslint-disable-next-line no-await-in-loop
        const statusRes = await api.get("student/wizard/ai-marking/status", {
          params: { session_id: sessionId },
        });
        const data = statusRes.data?.data || {};
        const status = String(data.status || "");
        if (data.message) setStatusMessage(String(data.message));
        if (status === "completed") {
          setSummary(data.summary || null);
          setQuestions(Array.isArray(data.questions) ? data.questions : []);
          setPhase("results");
          return;
        }
        if (status === "failed") {
          throw new Error(data.message || "Marking failed.");
        }
      }
      throw new Error("Marking is taking too long. Please try again.");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Could not mark your paper.";
      setError(msg);
      setPhase("capture");
      toast.error(msg);
    }
  };

  const pct = Number(summary?.percentage);
  const hasPct = Number.isFinite(pct);

  return (
    <WizardPageShell
      title="AI Marking"
      subtitle="Upload practice paper pages or a PDF for instant marking"
      maxWidth="md"
    >
      {phase === "capture" ? (
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Upload up to 8 photos or one PDF of your completed paper.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            hidden
            onChange={(e) => onPick(e.target.files)}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => inputRef.current?.click()}
            sx={{ textTransform: "none", alignSelf: "flex-start" }}
          >
            Choose files
          </Button>
          {files.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Selected ({files.length})
              </Typography>
              <Stack spacing={0.5}>
                {files.map((f) => (
                  <Typography key={f.name + f.size} variant="body2" color="text.secondary">
                    {f.name}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}
          <Button
            variant="contained"
            disabled={files.length === 0}
            onClick={startMarking}
            sx={{ textTransform: "none", fontWeight: 700, alignSelf: "flex-start" }}
          >
            Mark my paper
          </Button>
        </Stack>
      ) : null}

      {phase === "processing" ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography fontWeight={600}>{statusMessage || "Processing…"}</Typography>
          <Box sx={{ width: "100%", maxWidth: 360 }}>
            <LinearProgress />
          </Box>
        </Stack>
      ) : null}

      {phase === "results" ? (
        <Stack spacing={2}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "#FEF2F2",
              border: "1px solid #FECACA",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" fontWeight={800} color="#9F1239">
              {hasPct ? `${Math.round(pct)}%` : "—"}
            </Typography>
            <Typography color="text.secondary">
              {summary?.performance_level || "Marked"}
              {summary?.correct_count != null
                ? ` · ${summary.correct_count}/${summary.total_questions ?? "?"} correct`
                : ""}
            </Typography>
          </Box>

          {questions.map((q, idx) => (
            <Accordion key={idx} disableGutters elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                  <Typography fontWeight={700}>Q{q.number ?? idx + 1}</Typography>
                  <Chip
                    size="small"
                    label={q.is_correct ? "Correct" : "Incorrect"}
                    color={q.is_correct ? "success" : "error"}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                    {String(q.question_text || "").slice(0, 80)}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Typography variant="body2"><strong>Your answer:</strong> {q.student_answer || "—"}</Typography>
                  <Typography variant="body2"><strong>Correct:</strong> {q.correct_answer || "—"}</Typography>
                  {q.feedback ? <Typography variant="body2">{q.feedback}</Typography> : null}
                  {q.explanation ? (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                      {q.explanation}
                    </Typography>
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          <Button variant="outlined" onClick={reset} sx={{ textTransform: "none", alignSelf: "flex-start" }}>
            Mark another paper
          </Button>
        </Stack>
      ) : null}
    </WizardPageShell>
  );
}
